import GroupAddressTreeViewModel from "../modelTreeViews/GroupAddressTreeviewModel";
import BaseModel from "./BaseModel";
import BaseModelMap from "./BaseModelMap";
import BaseObject from "./BaseObject";
import { copyAllProperties, later } from "./utils";
import { showErrToast } from "../components/Toasts";
import KnxBridge from "../KnxBridge";
import type Signal from "./Signal";

import Logger from "../Logger";
const logger = new Logger();

//////////////////////////////////////////////////
const CLASSNAME = "GroupAddress"; // no itx

import ObjectFactory from "./ObjectFactory";
import { itx, T } from "../translationItx/itx";
import type Multiplexer from "./Multiplexer";

ObjectFactory.registerClass(CLASSNAME, (data?: any, parent?: BaseModel) => {
  return new GroupAddress(data, parent);
});
//////////////////////////////////////////////////

export default class GroupAddress extends BaseModel {
  public get className(): string {
    return CLASSNAME;
  }

  getParentCollectionName(): string | undefined {
    if (this.level >= 2) {
      return "children"; // no itx
    }

    return undefined;
  }

  // -----------------------------------------------------------------------------
  // isMC
  // -----------------------------------------------------------------------------
  private _isMC?: boolean;
  get isMC(): boolean | undefined {
    return this._isMC;
  }

  set isMC(newValue: boolean | undefined) {
    if (this._isMC === newValue) return;
    this.beforeChange("isMC");
    this._isMC = newValue;
    this.changed("isMC");
  }

  // -----------------------------------------------------------------------------

  get isManualConnected(): boolean {
    return this.isMC == true;
  }

  set isManualConnected(newValue: boolean) {
    this.isMC = false;
    if (newValue && !this.isManualGroupAddress) {
      this.isMC = newValue;
    }
  }

  // -----------------------------------------------------------------------------
  // fullAddress
  // -----------------------------------------------------------------------------
  _fullAddress: string = "";
  get fullAddress(): string {
    return this._fullAddress;
  }

  set fullAddress(newValue: string) {
    if (this._fullAddress === newValue) return;
    this.beforeChange("fullAddress");
    this._fullAddress = newValue;
    this.changed("fullAddress");
  }

  // -----------------------------------------------------------------------------
  // address
  // -----------------------------------------------------------------------------
  _address: string = "";
  get address(): string {
    return this._address;
  }

  set address(newValue: string) {
    if (this._address === newValue) return;
    this.beforeChange("address");
    this._address = newValue;
    this.changed("address");
  }

  // -----------------------------------------------------------------------------
  // isStatus
  // -----------------------------------------------------------------------------
  _isStatus: boolean = false;
  get isStatus(): boolean {
    return this._isStatus;
  }

  set isStatus(newValue: boolean) {
    if (this._isStatus === newValue) return;
    this.beforeChange("isStatus");
    this._isStatus = newValue;
    this.changed("isStatus");
  }

  // -----------------------------------------------------------------------------
  // generatorId
  // -----------------------------------------------------------------------------
  _generatorId: string | undefined = undefined;
  get generatorId(): string | undefined {
    return this._generatorId;
  }

  set generatorId(newValue: string | undefined) {
    if (this._generatorId === newValue) return;
    this.beforeChange("generatorId");
    this._generatorId = newValue;
    this.changed("generatorId");
  }

  // -----------------------------------------------------------------------------

  children = new BaseModelMap<GroupAddress>(GroupAddress, this, "g");

  // -----------------------------------------------------------------------------
  // level
  // -----------------------------------------------------------------------------
  _level = 0;
  get level(): number {
    return this._level;
  }

  set level(newValue: number) {
    if (this._level === newValue) return;
    this.beforeChange("level");
    this._level = newValue;
    this.changed("level");
  }

  // -----------------------------------------------------------------------------
  // dpt
  // -----------------------------------------------------------------------------
  _dpt = "1-1";
  get dpt(): string {
    return this._dpt;
  }

  set dpt(newValue: string) {
    if (this._dpt === newValue) return;
    this.beforeChange("dpt");
    this._dpt = newValue;
    this.changed("dpt");
  }

  // -----------------------------------------------------------------------------
  // signalTypeName
  // -----------------------------------------------------------------------------
  _signalTypeName = "";
  get signalTypeName(): string {
    return this._signalTypeName;
  }

  set signalTypeName(newValue: string) {
    if (this._signalTypeName === newValue) return;
    this.beforeChange("signalTypeName");
    this._signalTypeName = newValue;
    this.changed("signalTypeName");
  }

  // -----------------------------------------------------------------------------
  // isFixed
  // -----------------------------------------------------------------------------
  _isFixed = false;
  get isFixed(): boolean {
    return this._isFixed;
  }

  set isFixed(newValue: boolean) {
    if (this._isFixed === newValue) return;
    this.beforeChange("isFixed");
    this._isFixed = newValue;
    this.changed("isFixed");
  }

  // -----------------------------------------------------------------------------

  // set tag(newValue: string) {
  //   console.log("setTag " + newValue);
  //   this._tag = newValue;
  // }

  currentValue?: string; // non persistent
  receivedTimeStamp?: Date;
  sendTimeStamp?: Date;
  receivedCount?: number;

  //
  // Only used during import of ETS files
  //
  isMultiplexerGroupAddress?: boolean;

  getIsReferenced(hardwareSignalsOnly?: boolean): boolean {
    const bp = this.getSignalBackpointers(hardwareSignalsOnly);
    return bp.length > 0;
  }

  constructor(data?: any, parent?: BaseObject) {
    super(data, parent);
    this.treeViewModel = new GroupAddressTreeViewModel(this);
    copyAllProperties(data, this);

    this.updateFullAddress();
    this.isNew = true;
  }

  getTag() {
    //
    // das  "if (this)" ist höchst suspect.
    // ES ist notwendig, da es auch für neue Objekte aufgerufen innerhalb von copyProperties wird bei den
    // innerhalb der GruppenAddressen Generierung
    //
    if (this) {
      // if (his?.isManualGroupAddress && this?.selected) {
      //   return this.address;
      // }
    } else {
      console.log("this == null");
    }
    return this.fullAddress;
  }

  //
  // GroupAddress dons support relative tags
  //
  getRelativeTag(relativeObject: BaseModel): string {
    return this.getTag();
  }

  setTag(newValue: string) {
    if (this.isManualGroupAddress && this.selected) {
      if (this.address != newValue) {
        const parentGa = this.parent as GroupAddress;
        const allReadyExists = parentGa.children.findFirst((i) => i.address == newValue);
        if (!allReadyExists) {
          this.address = newValue;
          this.updateFullAddress();
        } else {
          showErrToast(itx("groupMonitor.addressInUse", "Group address already in use."));
        }
      }
    }

    const ss = newValue.split("/");
    if (this.level == 3 && ss.length == 3) {
      const lastFullAddress = this.fullAddress;
      const ssLast = lastFullAddress.split("/");
      if (ss.length == 3) {
        let level1: GroupAddress | undefined;
        let level2: GroupAddress | undefined;
        if (ssLast[0] != ss[0] || ss[1] != ssLast[1] || ssLast[2] != ss[2]) {
          level1 = state.addresses.findFirst((i) => i.address == ss[0]);
          if (!level1) {
            level1 = new GroupAddress({ address: ss[0], tag: ss[0] }, state.addresses);
            state.addresses.add(level1);
          }
          level2 = level1.children.findFirst((i) => i.address == ss[1]);
          if (!level2) {
            level2 = new GroupAddress({ address: ss[1], tag: ss[1] }, level1);
            level1.children.add(level2);
          }
          const level3 = level2.children.findFirst((i) => i.address == ss[2]);
          if (level3) {
            showErrToast(state.translate("groupMonitor.addressInUse", "Group address already in use."));
          } else {
            (this.parent as GroupAddress).children.remove(this);
            level2.children.add(this);
            this.parent = level2;
            this.address = ss[2];
            this.tag = ss[2];
            this.fullAddress = `${ss[0]}/${ss[1]}/${ss[2]}`;
            this.raiseOnChange("tag");
          }
        }
      } else {
        showErrToast(state.translate("groupMonitor.invalidAddress", "Invalid address, format should be x/x/x"));
      }
    } else {
      this._tag = newValue;
    }
  }

  get fullTag(): string {
    return this.fullAddress;
  }

  get simpleFullTag(): string {
    return this.fullAddress;
  }

  get isManualGroupAddress(): boolean {
    if (this.isManualConnected) {
      return true;
    }

    if (!this.fullAddress) {
      return false;
    }
    return this.fullAddress.startsWith("31/2/") || this.fullAddress.startsWith("31/3/");
  }

  get isManualGroupAddressFolder(): boolean {
    return this.fullAddress == "31/2" || this.fullAddress == "31/3";
  }

  get isEnvironmentFolder(): boolean {
    return this.fullAddress == "31/1";
  }

  get isEnvironmentAddress(): boolean {
    return this.fullAddress.startsWith("31/1/");
  }

  get isFixedGroupAddress(): boolean {
    if (!this.fullAddress) {
      return false;
    }
    return this.fullAddress.startsWith("31/1/");
  }

  canBeDeletedInteractive(): boolean {
    if (this.children.size == 0) {
      return true;
    } else {
      return false;
    }
  }

  getDeleteConfirmText(): string | undefined {
    if (!this.canBeDeletedInteractive()) {
      const T1 = this.fullTag;
      return itx(
        "groupMonitor.addressHasChildren",
        `Wollen sie wirklich T1 löschen? Es sind noch untergeordnete Gruppenadressen vorhanden.`,
        { T1: T1 },
      );
    } else {
      return super.getDeleteConfirmText();
    }
  }

  delete(withoutUpdate?: boolean): Array<BaseObject> {
    var res = new Array<BaseObject>();

    console.log("delete GroupAddress", this.fullAddress, this.name);

    this.isDeleting = true;

    const backpointers = this.getSignalBackpointers();
    // Save before clearing so undelete() can restore the connections.
    this.lastSignalBackpointers = [...backpointers];
    backpointers.forEach((signal) => {
      signal.groupAddresses.remove(this);
    });
    this._signalBackpointers = undefined;

    // delete all child addresses
    res = res.concat(this.children.deleteAllItems());

    // delete this
    res = res.concat(super.delete(withoutUpdate));
    return res;
  }

  /** Restores signal references and their backpointers; undo restores parent collection membership. */
  undelete() {
    super.undelete();
    // ReferenceArray.remove() does not record an undo operation for the signal reference.
    if (this.lastSignalBackpointers) {
      this._signalBackpointers = [...this.lastSignalBackpointers];
      for (const signal of this.lastSignalBackpointers) {
        if (!signal.groupAddresses.referencedIds.includes(this.id)) {
          signal.groupAddresses.add(this);
        }
        signal.raiseOnChange("groupAddresses");
      }
      this.lastSignalBackpointers = undefined;
    }
  }

  getPersistentProperties(): string[] {
    return [
      "address",
      "fullAddress", // no itx
      "name", // no itx
      "level", // no itx
      "isStatus", // no itx
      "generatorId", // no itx
      "children", // no itx
      "dpt", // no itx
      "signalTypeName", // no itx
      "isFixed", // no itx
      "isMC", // no itx
      ...super.getPersistentProperties(),
    ];
  }

  lastSignalBackpointers?: Array<Signal>;
  private _signalBackpointers: Array<Signal> | undefined = undefined;

  addSignalBackpointer(ref: Signal) {
    if (!this._signalBackpointers) {
      this._signalBackpointers = [];
    } else if (this._signalBackpointers.includes(ref)) {
      return;
    }

    this._signalBackpointers.push(ref);
  }

  removeSignalBackpointer(ref: Signal) {
    if (this._signalBackpointers) {
      const idx = this._signalBackpointers.indexOf(ref);
      if (idx >= 0) {
        this._signalBackpointers.splice(idx, 1);
      }
    }
  }

  //
  // Force rebuild of backpointers
  //
  clearSignalBackpointers() {
    this._signalBackpointers = undefined;
  }

  getSignalBackpointers(hardwareSignalsOnly?: boolean): Array<Signal> {
    if (!this._signalBackpointers) {
      const res = Array<Signal>();

      state.objectRegistry.allObjects.forEach((obj) => {
        if (obj.className == "Signal") {
          const signal = obj as Signal;

          signal.groupAddresses.forEach((groupAdress) => {
            if (groupAdress === this) {
              res.push(signal);
            } else {
              if (groupAdress.fullAddress === this.fullAddress) {
                console.error(
                  `getReferences error: ${this.fullAddress}, duplicate groupAdress Object: `,
                  groupAdress.simpleFullTag,
                  groupAdress,
                  groupAdress.parent,
                  groupAdress.parent?.parent,
                );
              }
            }
          });
        }
      });

      this._signalBackpointers = res;
    }

    if (hardwareSignalsOnly) {
      return this._signalBackpointers.filter(
        (signal) => (signal.parent as BaseModel)?.isChannel || (signal.parent as BaseModel)?.isButtonChannel,
      );
    } else {
      return this._signalBackpointers;
    }
  }

  get generatorObject(): BaseModel | undefined {
    return this.state.objectRegistry.getObject(this.generatorId);
  }

  set generatorObject(newValue: BaseModel | undefined) {
    this.generatorId = newValue?.id;
  }

  //////////////////////////////////////////////////////
  ///////////// for paste

  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      this.children.mapAfterCopy();
    }
  }

  static deleteAllReferencesToGroupAddresses(execptOfManual: boolean) {
    //
    // Clear References to all GroupAddresses ..
    //   ... in all signals of all channels in all devices
    //
    for (const device of state.allDevices()) {
      for (const channel of device.channels) {
        for (const signal of channel.signals) {
          signal.removeAllGroupAddress(execptOfManual);
        }
      }
    }

    //
    // löse Gruppenadressen von allen Consumern
    //
    for (const consumer of state.allConsumers()) {
      for (const signal of consumer.signals) {
        signal.removeAllGroupAddress(execptOfManual);
      }
    }

    //
    // löse Gruppenadressen von allen Multiplexern
    //
    for (const scene of state.allScenes()) {
      if (scene.isMultiplexer) {
        for (const signal of (scene as Multiplexer).signals) {
          // if (signal.groupAddresses.length > 0) {
          //   console.log(
          //     `removeAllGroupAddress: ${signal.simpleFullTag} : signal.groupAddresses.items.length = ${signal.groupAddresses.length}  `,
          //   );
          // }

          signal.removeAllGroupAddress(execptOfManual);

          // if (signal.groupAddresses.length > 0) {
          //   console.log(
          //     `removeAllGroupAddress FAILED: ${signal.simpleFullTag} : signal.groupAddresses.items.length = ${signal.groupAddresses.length}  `,
          //   );
          // }

          // if (isDevelopmentEnvironment()) {
          //   signal.groupAddresses.forEach((ga) => {
          //     console.log(`GA NOT deleted: ${signal.simpleFullTag} -  ${ga.fullAddress}`);
          //   });
          // }
        }
      }
    }

    state.allRooms().forEach((room) => {
      if (room.groupAddress?.isDeleted) {
        room.groupAddress = undefined;
      }
    });
  }

  sendValueToKnxBridge(v: string) {
    this.currentValue = v;
    this.receivedTimeStamp = undefined;
    this.sendTimeStamp = new Date();
    this.raiseOnChange("currentValue");
    KnxBridge.sendGroupAddressValue(this, v);
  }

  get isStatusGroupAddress(): Boolean {
    for (let [key, value] of state.objectRegistry.allObjects) {
      const obj = value;

      if (obj && obj.className == "Signal" && obj.parent?.className == "Channel") {
        const signal = obj as Signal;
        if (signal.groupAddresses.referencedIds.includes(this.id)) {
          return signal.type.isStatus;
        }
      }
    }

    return false;
  }

  ///////////////////////////////////////////////////////////

  updateFullAddress() {
    if (this.parent) {
      if (this.level == 1) {
        this.fullAddress = this.address;
      } else {
        this.fullAddress = (this.parent as GroupAddress).fullAddress + "/" + this.address;
      }
    }
  }

  checkParentConsistency() {
    if (this.parent) {
      if (this.parent.className == "GroupAddress") {
        const parentGa = this.parent as GroupAddress;

        if (this.level >= 2) {
          if (!parentGa.children.findFirst((c) => c == this)) {
            this.logConsistencyError(`GroupAddress ${this.fullAddress} is not in parent ${parentGa.fullAddress}`);
          }
        } else {
          if (!state.addresses.findFirst((c) => c == this)) {
            this.logConsistencyError(`GroupAddress ${this.fullAddress} is not in parent ${parentGa.fullAddress}`);
          }
        }

        if (this.level <= 1) {
          parentGa.checkParentConsistency();
        }
      }
    }
  }

  multibleDPtsSizes(): boolean {
    const dptSizes = new Set<number>();
    this.getSignalBackpointers().forEach((signal) => {
      const size = signal.datapointTypeSize();
      if (size > 0) {
        dptSizes.add(size);
      } else {
        logger.error(
          `Unknown DPT size for signal: ${signal.simpleFullTag}, datapoint type: ${signal.knxDatapointType}`,
        );
      }
    });
    return dptSizes.size > 1;
  }

  consistencyCheck(parent: BaseModel) {
    super.consistencyCheck(parent);

    GroupAddress.checkDuplicateGroupAddresses(this.children);

    // check fullAddress
    const expectedFullAddress =
      this.level == 1 ? this.address : (this.parent as GroupAddress).fullAddress + "/" + this.address;
    if (this.fullAddress != expectedFullAddress) {
      this.logConsistencyError(`GroupAddress ${this.fullAddress} does not match expected ${expectedFullAddress}`);
    }

    if (this.level == 3) {
      if (this.getSignalBackpointers().length == 0) {
        this.logConsistencyError(`GroupAddress ${this.fullAddress} without any reference`);
      }
      // check here with other errors
      if (this.multibleDPtsSizes() && this.isManualConnected) {
        this.logConsistencyError(
          itx("GroupAddress.multipleDPTSizes", `GroupAddress T1 has multiple DPT sizes`, {
            T1: this.fullAddress,
          }),
          "multipleDPTSizes",
        );
      }
    } else {
      this.children.forEach((child) => {
        if (!child.parent) {
          child.logConsistencyError(`GroupAddress ${child.fullAddress} has NO parent`);
        } else if (child.parent != this) {
          child.logConsistencyError(
            `GroupAddress ${child.fullAddress} has invalid parent ${child.parent?.className}: ${child.parent?.simpleFullTag}`,
          );
        }
        child.consistencyCheck(this);
      });
    }
  }

  static checkDuplicateGroupAddresses(addressCollection: BaseModelMap<GroupAddress>) {
    // check childeren for duplicates
    const addresses = new Set<string>();
    addressCollection.forEach((child) => {
      if (addresses.has(child.address)) {
        child.logConsistencyError(`Duplicate GroupAddress ${child.fullAddress}`, "duplicateGroupAddress");
      } else {
        addresses.add(child.address);
      }
    });
  }

  static createNewGroupAddress(params: {
    parent: GroupAddress | BaseModelMap<GroupAddress>;
    level: number;
    name: string;
    tag?: string;
    address?: number;
  }): GroupAddress {
    const { parent, level, name } = params;
    var { address } = params;

    const x = parent.className == "GroupAddress" ? (parent as GroupAddress).children : parent;

    const parentCollection = x as BaseModelMap<GroupAddress>;

    if (address == undefined) {
      let m = 255;
      let start = 0;

      switch (level) {
        case 1:
          m = 31;
          break;
        case 2:
          m = 7;
          break;
        case 3:
          m = 255;
          start = 1;

          break;
      }

      const sortedGroupAddresses = parentCollection.sortIntBy("address");
      if (level == 3) {
        //
        // find free address (append after biggest address)
        //

        let last = 0;
        let l = sortedGroupAddresses.length;
        if (l > 0) {
          last = parseInt(sortedGroupAddresses[l - 1].address) + 1;
        }
        address = Math.max(start, last);
      } else {
        let free = 0;
        while (free <= m && sortedGroupAddresses[free]?.address == `${free}`) {
          free++;
        }

        address = free;
      }
    }

    const ga = new GroupAddress({}, parent);
    parentCollection.addWithoutUpdate(ga);

    ga.address = `${address}`;
    ga.level = level;
    ga.name = name;
    ga.isNew = true;
    ga.updateFullAddress();

    console.log(`createNewGroupAddress ${ga.fullAddress} ${ga.name}`, ga);
    // check for errors

    return ga;
  }

  findFirstFreeAddress(): number {
    let freeAddresses = this.getFreeChildAddress();
    let res = Math.min(...freeAddresses);
    return res;
  }

  getFreeChildAddress(): Set<number> {
    let freeAddresses = new Set<number>();
    for (let i = 1; i <= 255; i++) {
      freeAddresses.add(i);
    }

    this.children.forEach((child) => {
      const addr = parseInt(child.address);
      freeAddresses.delete(addr);
    });

    return freeAddresses;
  }
  ///////////////////////////////////////////////////////////

  static moveAllManualGroupAddressesToManualGroupAddressFolder() {
    const a2 = "2";

    // Stelle sicher, dass die globalen Adressen existieren
    let manualGroup = state.addresses.findFirst((a) => a.address == "31")?.children.findFirst((a) => a.address == a2);

    if (!manualGroup) {
      manualGroup = new GroupAddress(
        { address: a2, tag: a2 },
        state.addresses.findFirst((a) => a.address == "31"),
      );
      state.addresses.add(manualGroup);
    }

    if (!manualGroup) {
      console.error("manualGroup not found");
      return;
    }

    state.addresses.forEach((a1) => {
      if (a1.address != "31") {
        a1.children.forEach((a2) => {
          a2.children.forEach((a3) => {
            if (a3.isManualGroupAddress) {
              a2.children.remove(a3);
              a3.parent = manualGroup;
              manualGroup?.children.add(a3);

              a3.address = `${manualGroup.findFirstFreeAddress()}`;

              a3.updateFullAddress();
              a3.raiseOnChange("fullAddress");
            }
          });
        });
      }
    });
  }

  //
  // All Manual inside the normal hierarchy will be moved to the manual group address folder
  //
  static deleteAllGroupAddressesExceptOfManuals() {
    // disabled MWE 19.5.2026
    //
    // this.moveAllManualGroupAddressesToManualGroupAddressFolder();

    state.addresses.forEach((a1) => {
      a1.children.forEach((a2) => {
        a2.children.removeIf((a3) => !a3.isManualGroupAddress);
      });
      a1.children.removeIf((a2) => a2.children.size == 0 && !a2.isManualGroupAddressFolder);
    });

    state.addresses.removeIf((a1) => a1.children.size == 0);
  }

  static updateAllBackpointers() {
    state.allLevel3GroupAddresses().forEach((ga) => {
      ga.clearSignalBackpointers();
    });

    [...state.allSignals(), ...state.allConsumerSignals(), ...state.allMultiplexerSignals()].forEach((signal) => {
      signal.groupAddresses.items.forEach((ga) => {
        ga.addSignalBackpointer(signal);
      });
    });
  }
}
